// The match engine: one pure function turns (state, command, time) into the next state.
// It runs on the server (the source of truth) and is imported by the screens for clock maths.
import { getRules, type Rules } from './rules';

export type Side = 'home' | 'away';
export const SIDES: Side[] = ['home', 'away'];

export interface Team { name: string; short: string; color: string; crest?: string | null }

export interface ScoreEvent { id: number; t: 'score'; team: Side; kind: string; pts: number; at: number; phase: string; player?: string | null }
export interface CardEvent { id: number; t: 'card'; team: Side; card: string; at: number; phase: string; number?: string | null; endsAt: number | null; cleared?: boolean }
export type MatchEvent = ScoreEvent | CardEvent;

export interface Moment {
  eventId: number; team: Side; kind: string; label: string; pts: number; player: string | null; startedAt: number; durationMs: number;
  next?: Moment | null;               // a conversion kicked during its try's celebration: shown as soon as the try's finishes
}

export type Phase = string;           // 'pre' | a period id ('1H', '2H') | 'HT' | 'FT'
export type View = 'board' | 'sponsors' | 'teams' | 'off' | 'video';

/** Today's teams: our players (Studio ids) with today's shirt numbers, and the opponents' names as typed in. */
export interface Lineups { home: { player: string; number: string }[]; away: { name: string; number: string }[] }

/** A promo video playing on the screen (half-time bar offers, a sponsor's advert). */
export interface PlayingVideo { id: string; url: string; title: string; durationMs: number | null; loop: boolean; startedAt: number }
/** The sponsor given pride of place for this match. */
export interface MatchSponsor { name: string; logo: string; bg: string }

export interface MatchState {
  v: 1;
  matchId: string;
  rulesId: string;
  created: number;
  teams: Record<Side, Team>;
  squad: string | null;               // which home squad is playing ('women', 'men')
  phase: Phase;
  clock: { running: boolean; baseMs: number; accMs: number; since: number | null };
  events: MatchEvent[];
  nextId: number;
  view: View;
  moment: Moment | null;
  video?: PlayingVideo | null;        // optional: matches saved before videos existed have neither
  matchSponsor?: MatchSponsor | null;     // older matches: one match day sponsor, all club sponsors shown
  /** The sponsors chosen for this match, in screen order; those marked matchday get the "Today's match day sponsor" label.
   *  Missing = show all the club's sponsors (matches started before this existed). */
  sponsors?: (MatchSponsor & { matchday: boolean })[] | null;
  lineups?: Lineups | null;
  /** false for a cup match or friendly: no league bonus points at full time. Missing (older matches) = league. */
  league?: boolean;
  /** Football: the added time shown on the fourth official's board for the half being played. */
  added?: { phase: string; min: number } | null;
  seq: number;
}

export type Command =
  | { c: 'score'; team: Side; kind: string; player?: string | null }
  | { c: 'card'; team: Side; card: string; number?: string | null }
  | { c: 'clearCard'; id: number }
  | { c: 'undo' }
  | { c: 'clock'; run: boolean }
  | { c: 'clockAdjust'; deltaMs: number }
  | { c: 'clockSet'; ms: number }
  | { c: 'phase'; to: Phase }
  | { c: 'view'; to: Exclude<View, 'video'> }
  | { c: 'video'; id: string; url: string; title: string; durationMs: number | null; loop: boolean }   // url etc. filled in by the service from Studio's list
  | { c: 'videoStop' }
  | { c: 'lineup'; home: unknown; away: unknown }      // sent by Matchday setup (admin only)
  | { c: 'added'; min: number }                        // football: added time, 0 takes it off
  | { c: 'endMoment' };

export const MOMENT_MS = 6000;
const MIN = 60_000;

export class CommandError extends Error {}

export function newMatch(o: { matchId: string; rulesId?: string; home: Team; away: Team; squad?: string | null; matchSponsor?: MatchSponsor | null; sponsors?: (MatchSponsor & { matchday: boolean })[] | null; league?: boolean }, now: number): MatchState {
  return {
    v: 1, matchId: o.matchId, rulesId: o.rulesId ?? 'rugby-union', created: now,
    teams: { home: o.home, away: o.away }, squad: o.squad ?? null,
    phase: 'pre', clock: { running: false, baseMs: 0, accMs: 0, since: null },
    events: [], nextId: 1, view: 'board', moment: null, video: null, matchSponsor: o.matchSponsor ?? null, ...(o.sponsors ? { sponsors: o.sponsors } : {}), league: o.league ?? true, seq: 0,
  };
}

/** The video the screen should be showing now, if any. A play-once video ends by itself and the scoreboard returns. */
/** The celebration on screen right now, if any: a try's, then the conversion queued behind it. */
export function momentShowing(s: MatchState, now: number): Moment | null {
  for (const m of [s.moment, s.moment?.next]) if (m && now >= m.startedAt && now < m.startedAt + m.durationMs) return m;
  return null;
}

export function videoPlaying(s: MatchState, now: number): PlayingVideo | null {
  const v = s.view === 'video' ? s.video : null;
  if (!v) return null;
  if (v.loop || v.durationMs == null) return v;
  return now < v.startedAt + v.durationMs ? v : null;
}

/** Clock reading in match milliseconds (rugby: 0–40:00 first half, 40:00–80:00 second; football 0–45, 45–90). */
export function matchMs(s: MatchState, now: number): number {
  const { running, baseMs, accMs, since } = s.clock;
  return baseMs + accMs + (running && since != null ? Math.max(0, now - since) : 0);
}

/** The clock has reached the end of the half being played (40:00, or 80:00 in the second half):
 *  the next time the ball goes dead, the referee blows for half-time or full-time. */
export function timeUp(s: MatchState, now: number, rules: Rules = getRules(s.rulesId)): boolean {
  const period = rules.periods.find(p => p.id === s.phase);
  return !!period && matchMs(s, now) >= (period.startMin + period.lengthMin) * MIN;
}

export interface SideSummary {
  points: number;
  tries: number;
  /** each kind of score with how many, in the rules' order: [{label:'Try', n:3}, …] */
  counts: { kind: string; label: string; n: number }[];
  /** try or goal scorers (player ids; null for own goals) with how many and the minutes, most first */
  scorers: { player: string | null; tries: number; mins: string[] }[];
  bonus: ('try' | 'losing')[];
}
export interface Summary { home: SideSummary; away: SideSummary; result: Side | 'draw' }

/** The full-time summary: who won, what each side scored, the try scorers and any league bonus points. */
export function matchSummary(s: MatchState, rules: Rules = getRules(s.rulesId)): Summary {
  const t = totals(s);
  const side = (team: Side): SideSummary => {
    const mine = s.events.filter((e): e is ScoreEvent => e.t === 'score' && e.team === team);
    const counts = rules.scoring.map(x => ({ kind: x.kind, label: x.label, n: mine.filter(e => e.kind === x.kind).length })).filter(c => c.n > 0);
    const tryKinds = new Set(rules.scoring.filter(x => x.isTry).map(x => x.kind));
    const ownGoal = new Set(rules.scoring.filter(x => x.ownGoal).map(x => x.kind));
    const by = new Map<string | null, string[]>();
    for (const e of mine) {
      const who = ownGoal.has(e.kind) ? null : e.player;
      if (tryKinds.has(e.kind) && (who || ownGoal.has(e.kind))) by.set(who ?? null, [...(by.get(who ?? null) ?? []), minuteOf(e, rules)]);
    }
    return { points: t[team], tries: mine.filter(e => tryKinds.has(e.kind)).length, counts,
      scorers: [...by].map(([player, mins]) => ({ player, tries: mins.length, mins })).sort((a, b) => b.tries - a.tries), bonus: [] };
  };
  const home = side('home'), away = side('away');
  if (rules.bonus && s.league !== false) {
    for (const [me, them] of [[home, away], [away, home]] as const) {
      if (me.tries >= rules.bonus.tries) me.bonus.push('try');
      if (me.points < them.points && them.points - me.points <= rules.bonus.losingMargin) me.bonus.push('losing');
    }
  }
  return { home, away, result: t.home > t.away ? 'home' : t.away > t.home ? 'away' : 'draw' };
}

/** The minute something happened, as football writes it: 23', or 45+2' in first-half added time. */
export function minuteOf(e: { at: number; phase: string }, rules: Rules): string {
  const min = Math.floor(e.at / MIN) + 1, period = rules.periods.find(p => p.id === e.phase);
  const end = period ? period.startMin + period.lengthMin : null;
  return end != null && min > end ? `${end}+${min - end}'` : `${min}'`;
}

/** Football: red cards stay by the team's name for the rest of the match. */
export function boardCards(s: MatchState, side: Side, rules: Rules = getRules(s.rulesId)): CardEvent[] {
  const on = new Set(rules.cards.filter(c => c.onBoard).map(c => c.card));
  return s.events.filter((e): e is CardEvent => e.t === 'card' && e.team === side && on.has(e.card) && !e.cleared);
}

/** The added time to show next to the clock ("+3"), for the half being played only. */
export function addedTime(s: MatchState): number {
  return s.added && s.added.phase === s.phase ? s.added.min : 0;
}

export function totals(s: MatchState): Record<Side, number> {
  const t = { home: 0, away: 0 };
  for (const e of s.events) if (e.t === 'score') t[e.team] += e.pts;
  return t;
}

/** Cards still on the screen: yellow cards with time left, flashing once they reach zero until cleared. */
export function activeCards(s: MatchState, now: number) {
  const m = matchMs(s, now);
  return s.events
    .filter((e): e is CardEvent => e.t === 'card' && !e.cleared && e.endsAt != null)
    .map(e => ({ ...e, remainingMs: Math.max(0, (e.endsAt as number) - m) }));
}

export function phaseLabel(s: MatchState, rules: Rules = getRules(s.rulesId)): string {
  if (s.phase === 'pre') return 'Kick-off soon';
  if (s.phase === 'HT') return 'Half-time';
  if (s.phase === 'FT') return 'Full-time';
  return rules.periods.find(p => p.id === s.phase)?.label ?? s.phase;
}

export function describe(e: MatchEvent, s: MatchState, nameOf: (player: string) => string = p => p): string {
  const rules = getRules(s.rulesId);
  const team = s.teams[e.team].name;
  const min = minuteOf(e, rules);
  const who = e.t === 'score' && e.player ? nameOf(e.player) : '';
  if (e.t === 'score') return `${rules.scoring.find(x => x.kind === e.kind)?.label ?? e.kind} · ${team}${who ? ` · ${who}` : ''} · ${min}`;
  return `${rules.cards.find(x => x.card === e.card)?.label ?? 'Card'} · ${team}${e.number ? ` #${e.number}` : ''} · ${min}`;
}

export function fmtClock(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

const isSide = (x: unknown): x is Side => x === 'home' || x === 'away';

/** Put the clock on an exact reading. A running clock carries on from there; sin-bin players keep the time they still owe. */
function setClock(s: MatchState, targetMs: number, now: number) {
  const shift = targetMs - matchMs(s, now);
  s.clock.accMs = targetMs - s.clock.baseMs;
  if (s.clock.running) s.clock.since = now;
  for (const e of s.events) if (e.t === 'card' && e.endsAt != null && !e.cleared) e.endsAt += shift;
}

const shirt = (v: unknown) => String(v ?? '').replace(/\D/g, '').slice(0, 3);
const byShirt = <T extends { number: string }>(a: T, b: T) => (Number(a.number) || 999) - (Number(b.number) || 999);
/** Today's teams as Matchday setup sends them, tidied: shirt numbers are digits, names are trimmed, 30 a side at most, in shirt order. */
export function cleanLineups(home: unknown, away: unknown): Lineups {
  const list = (x: unknown) => (Array.isArray(x) ? x : []).slice(0, 30) as Record<string, unknown>[];
  const seen = new Set<string>();
  const h = list(home).flatMap(r => {
    const player = String(r?.player ?? '').slice(0, 60);
    if (!player || seen.has(player)) return [];
    seen.add(player);
    return [{ player, number: shirt(r.number) }];
  });
  const a = list(away).flatMap(r => { const name = String(r?.name ?? '').replace(/\s+/g, ' ').trim().slice(0, 40); return name ? [{ name, number: shirt(r.number) }] : []; });
  return { home: h.sort(byShirt), away: a.sort(byShirt) };
}

/** The opponents' team typed as lines like "1 John Smith" (or just names, numbered in order). */
export function parseTeamText(text: string): { name: string; number: string }[] {
  let next = 1;
  return text.split(/\r?\n/).map(l => l.trim()).filter(Boolean).slice(0, 30).map(l => {
    const m = l.match(/^(\d{1,3})[\s.):-]+(.+)$/);
    if (m) { next = Number(m[1]) + 1; return { number: m[1], name: m[2].trim() }; }
    return { number: String(next++), name: l };
  });
}

export function apply(prev: MatchState, cmd: Command, now: number): MatchState {
  const s: MatchState = structuredClone(prev);
  const rules = getRules(s.rulesId);
  const m = matchMs(s, now);

  switch (cmd.c) {
    case 'score': {
      if (!isSide(cmd.team)) throw new CommandError('Unknown team');
      const type = rules.scoring.find(x => x.kind === cmd.kind);
      if (!type) throw new CommandError(`Unknown score type ${cmd.kind}`);
      const player = typeof cmd.player === 'string' ? cmd.player.slice(0, 60) : null;
      const ev: ScoreEvent = { id: s.nextId++, t: 'score', team: cmd.team, kind: type.kind, pts: type.pts, at: m, phase: s.phase, player };
      s.events.push(ev);
      if (!type.moment) break;
      // a conversion kicked while its try is still being celebrated waits its turn, so the try's screen plays out first
      const cur = s.moment, curType = cur && rules.scoring.find(x => x.kind === cur.kind);
      const afterTry = !!cur && cur.team === ev.team && curType?.follows === type.kind && now < cur.startedAt + cur.durationMs;
      const moment: Moment = { eventId: ev.id, team: ev.team, kind: type.kind, label: type.label, pts: type.pts, player, startedAt: afterTry ? cur!.startedAt + cur!.durationMs : now, durationMs: MOMENT_MS };
      if (afterTry) cur!.next = moment;
      else s.moment = moment;
      break;
    }
    case 'card': {
      if (!isSide(cmd.team)) throw new CommandError('Unknown team');
      const type = rules.cards.find(x => x.card === cmd.card);
      if (!type) throw new CommandError(`Unknown card ${cmd.card}`);
      const number = typeof cmd.number === 'string' ? cmd.number.slice(0, 3) : null;
      s.events.push({ id: s.nextId++, t: 'card', team: cmd.team, card: type.card, at: m, phase: s.phase, number, endsAt: type.minutes == null ? null : m + type.minutes * MIN });
      break;
    }
    case 'clearCard': {
      const e = s.events.find(x => x.id === cmd.id && x.t === 'card') as CardEvent | undefined;
      if (!e) throw new CommandError('Card not found');
      e.cleared = true;
      break;
    }
    case 'undo': {
      const last = s.events.pop();
      if (!last) throw new CommandError('Nothing to undo');
      if (s.moment?.eventId === last.id) s.moment = null;
      else if (s.moment?.next?.eventId === last.id) s.moment.next = null;
      break;
    }
    case 'clock': {
      if (cmd.run && !s.clock.running) { s.clock.running = true; s.clock.since = now; }
      if (!cmd.run && s.clock.running) { s.clock.accMs = m - s.clock.baseMs; s.clock.running = false; s.clock.since = null; }
      break;
    }
    case 'clockAdjust': {
      const d = Number(cmd.deltaMs);
      if (!Number.isFinite(d) || Math.abs(d) > 10 * MIN) throw new CommandError('Clock adjustment out of range');
      setClock(s, Math.max(0, m + d), now);
      break;
    }
    case 'clockSet': {
      // the referee resets the match clock to an exact time, e.g. "23:40"
      const target = Math.round(Number(cmd.ms) / 1000) * 1000;
      if (!Number.isFinite(target) || target < 0 || target >= 120 * MIN) throw new CommandError('Enter a time between 00:00 and 119:59');
      setClock(s, target, now);
      break;
    }
    case 'phase': {
      const period = rules.periods.find(p => p.id === cmd.to);
      if (!period && !['pre', 'HT', 'FT'].includes(cmd.to)) throw new CommandError(`Unknown phase ${cmd.to}`);
      // stop the clock at the whistle
      if (s.clock.running) { s.clock.accMs = m - s.clock.baseMs; s.clock.running = false; s.clock.since = null; }
      if (period && s.phase !== period.id) {
        const before = matchMs(s, now);
        s.clock = { running: false, baseMs: period.startMin * MIN, accMs: 0, since: null };
        // keep sin-bin time owed across the half-time jump (e.g. 41:30 in the first half back to 40:00)
        const shift = matchMs(s, now) - before;
        for (const e of s.events) if (e.t === 'card' && e.endsAt != null && !e.cleared) e.endsAt += shift;
      }
      if (s.phase !== cmd.to) s.added = null;
      s.phase = cmd.to;
      break;
    }
    case 'view': {
      if (!['board', 'sponsors', 'teams', 'off'].includes(cmd.to)) throw new CommandError('Unknown view');
      s.view = cmd.to;
      s.video = null;
      break;
    }
    case 'video': {
      if (!cmd.id || !cmd.url) throw new CommandError('That video is not in Studio any more');
      const d = cmd.durationMs != null && Number.isFinite(cmd.durationMs) && cmd.durationMs > 0 ? Math.round(cmd.durationMs) : null;
      s.video = { id: String(cmd.id), url: String(cmd.url), title: String(cmd.title ?? '').slice(0, 60), durationMs: d, loop: !!cmd.loop, startedAt: now };
      s.view = 'video';
      s.moment = null;
      break;
    }
    case 'videoStop': {
      s.video = null;
      if (s.view === 'video') s.view = 'board';
      break;
    }
    case 'endMoment': s.moment = null; break;
    case 'lineup': s.lineups = cleanLineups(cmd.home, cmd.away); break;
    case 'added': {
      if (!rules.addedTime) throw new CommandError('Added time is for football');
      if (!rules.periods.some(p => p.id === s.phase)) throw new CommandError('Added time is shown during a half');
      const min = Math.round(Number(cmd.min));
      if (!Number.isFinite(min) || min < 0 || min > 15) throw new CommandError('Added time is 0 to 15 minutes');
      s.added = min ? { phase: s.phase, min } : null;
      break;
    }
    default: throw new CommandError('Unknown command');
  }
  s.seq++;
  return s;
}
